import { useState, useEffect, useCallback } from "react";

export type WidgetType = "aqi" | "pm25" | "pm10" | "temperature" | "humidity" | "pressure";

export type Widget = {
  id: string;
  type: WidgetType;
  order: number;
};

const WIDGETS_STORAGE_KEY = "homeWidgets";

// Dostępne typy widżetów
export const AVAILABLE_WIDGET_TYPES: { type: WidgetType; label: string; icon: string }[] = [
  { type: "aqi", label: "Jakość powietrza (AQI)", icon: "🌬️" },
  { type: "pm25", label: "PM2.5", icon: "💨" },
  { type: "pm10", label: "PM10", icon: "🌫️" },
  { type: "temperature", label: "Temperatura", icon: "🌡️" },
  { type: "humidity", label: "Wilgotność", icon: "💧" },
  { type: "pressure", label: "Ciśnienie", icon: "📊" },
];

// Funkcja do pobierania widżetów z localStorage
function getSavedWidgets(): Widget[] {
  try {
    const saved = localStorage.getItem(WIDGETS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("Error reading widgets from localStorage:", error);
  }
  return [];
}

// Funkcja do zapisywania widżetów do localStorage
function saveWidgets(widgets: Widget[]): void {
  try {
    localStorage.setItem(WIDGETS_STORAGE_KEY, JSON.stringify(widgets));
    // Wyślij custom event dla synchronizacji między instancjami hooka w tej samej karcie
    window.dispatchEvent(new CustomEvent('widgetsStorageChanged', { 
      detail: { widgets } 
    }));
  } catch (error) {
    console.warn("Error saving widgets to localStorage:", error);
  }
}

export function useWidgets() {
  const [widgets, setWidgets] = useState<Widget[]>(() => getSavedWidgets());

  // Zapisz do localStorage przy każdej zmianie
  useEffect(() => {
    saveWidgets(widgets);
  }, [widgets]);

  // Nasłuchuj zmian w localStorage (synchronizacja między instancjami hooka)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === WIDGETS_STORAGE_KEY && e.newValue) {
        try {
          const newWidgets = JSON.parse(e.newValue);
          setWidgets(newWidgets);
        } catch (error) {
          console.warn("Error parsing widgets from storage event:", error);
        }
      }
    };

    // Custom event dla synchronizacji w tej samej karcie
    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail?.widgets) {
        setWidgets(e.detail.widgets);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('widgetsStorageChanged', handleCustomStorageChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('widgetsStorageChanged', handleCustomStorageChange as EventListener);
    };
  }, []);

  const addWidget = useCallback((type: WidgetType) => {
    setWidgets(prev => {
      // Sprawdź czy widżet tego typu już istnieje
      if (prev.some(w => w.type === type)) {
        return prev; // Nie dodawaj duplikatu
      }

      const newWidget: Widget = {
        id: `${type}-${Date.now()}`,
        type,
        order: prev.length,
      };

      return [...prev, newWidget];
    });
  }, []);

  const removeWidget = useCallback((id: string) => {
    setWidgets(prev => {
      const filtered = prev.filter(w => w.id !== id);
      // Przepisz kolejność
      return filtered.map((w, index) => ({ ...w, order: index }));
    });
  }, []);

  const reorderWidgets = useCallback((newOrder: Widget[]) => {
    const reordered = newOrder.map((w, index) => ({ ...w, order: index }));
    setWidgets(reordered);
  }, []);

  const getAvailableTypes = useCallback(() => {
    const usedTypes = new Set(widgets.map(w => w.type));
    return AVAILABLE_WIDGET_TYPES.filter(w => !usedTypes.has(w.type));
  }, [widgets]);

  return {
    widgets,
    addWidget,
    removeWidget,
    reorderWidgets,
    getAvailableTypes,
  };
}

